<template>
  <div>
    <h4 class="p-1">Memory Game</h4>
    <p>
      Click on the cards to find the matching pairs. By completing the memory game you will receive points to increase your online activity score!<br/>
      The highest online activity score win a ticket to #GMF26.
    </p>
    <p>
      <small>
        Rules: Click on two cards to flip them over and find matching pairs of famous landmarks in North Rhine-Westphalia.
        The game will track your progress until you have found them all.
        Try to match all the pairs in the fewest possible turns to get the highest score!
      </small>
    </p>
  </div>
  <div class="memory">
    <div v-if="errorstring.de !== ''" class="alert alert-danger mb-2">
      {{ store.getters.i18n(errorstring) }}
    </div>

    <p>
      My best score: {{ score_reaction > 0 ? `${score_reaction} Attempts` : '-'}} <br/>
      Turn: {{ turn }}
    </p>

    <button v-if="allPairsAreFound" class="btn btn-primary btn-lg" @click="replay">Play again</button>

    <br/>
    <br/>

    <div class="memory-container">
      <div v-for="(card, index) in deckOfCards" @click="onClick(card, index)"
           :class="['memory-card', isFlippedClass(card)]"
           :key="card.id">
        <div class="front"></div>
        <div class="back">
          <img :src="card.content.url" alt="Card">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";

import {AppNetworkErrorOther, networkErrorText} from "@/crud"
import {TranslatedText} from "@/store/types";

import image1 from '@/assets/memory/1.jpg';
import image2 from '@/assets/memory/2.jpg';
import image3 from '@/assets/memory/3.jpg';
import image4 from '@/assets/memory/4.jpg';
import image5 from '@/assets/memory/5.jpg';
import image6 from '@/assets/memory/6.jpg';

let timer: number | undefined = undefined;

const faces = [
  {url: image1},
  {url: image2},
  {url: image3},
  {url: image4},
  {url: image5},
  {url: image6},
]

type Card = {
  id: number;
  content: { url: string };
  isPairFound: boolean;
}

type FlippedCard = Card & { index: number};

const turn  = ref(0);
const flippedCards = ref<FlippedCard[]>([]);
const deckOfCards = ref<Card[]>([]);
const cards = ref<Card[]>([]);
const allPairsAreFound = ref(false);

const isFlippedClass = ({id, isPairFound}) => {
  return {
    flipped: flippedCards.value.find((x) => x.id === id),
    matchingPair: isPairFound
  };
};

const onClick = (card: Card, index: number) => {
  if(flippedCards.value.find((x) => x.id === card.id)) {
    return;
  }

  if (!card.isPairFound) {
    clearTimeout(timer);
    flippedCards.value = flippedCards.value.length === 2
        ? [{...card, index}]
        : [...flippedCards.value, {...card, index}];
  }
};

const shuffle = (cards: Card[]) => {
  return cards.sort(() => Math.random() - 0.5);
};

const replay = () => {
  clearTimeout(timer);
  turn.value = 0;
  flippedCards.value = [];
  cards.value = cards.value.map((x) => {
    return {...x, isPairFound: false};
  });
  deckOfCards.value = shuffle(cards.value);
};

onMounted(() => {
  // Create all cards obj
  cards.value = faces.map((x, index) => {
    return {id: index, content: x, isPairFound: false};
  });

  // Dupplicate obj to get pairs and concat them to the existing array
  cards.value = cards.value.concat(
      cards.value.slice().map((card) => {
        return {
          ...card,
          id: cards.value.length + card.id,
        };
      })
  );

  deckOfCards.value = shuffle(cards.value);
});

watch(
    () => flippedCards.value,
    (cards) => {
      if (cards.length === 2) {
        turn.value++;
        if (cards[0].content === cards[1].content) {
          deckOfCards.value[cards[0].index].isPairFound = true;
          deckOfCards.value[cards[1].index].isPairFound = true;
        } else if (cards[0].content !== cards[1].content) {
          timer = setTimeout(
              () => flippedCards.value = [],
              2000
          );
        }
        allPairsAreFound.value = deckOfCards.value.every((x) => x.isPairFound);
      }
    }
);

// scoring logic

const store = useStore();

const score_reaction = computed(() => {
  return store.state.i.me.score_reaction;
});

const errorstring = ref<TranslatedText>({de: '', en: ''});

watch(
    () => allPairsAreFound.value,
    async (allPairsAreFound) => {
      if(allPairsAreFound) {
        await store.dispatch('i/setDataRankingE', 5);

        if (turn.value < score_reaction.value || score_reaction.value === 0) {
          try {
            errorstring.value = {de: '', en: ''}
            const dpr = await store.dispatch("i/setDataCompetition", {score_reaction: turn.value})
            if (!dpr.sv) {
              errorstring.value = {
                de: "Feld konnte nicht gesetzt werden.",
                en: "Could not set Field."
              }
            }
          } catch (e) {
            errorstring.value = networkErrorText(e as AppNetworkErrorOther)
          }
        }
      }
    }
);

</script>

<style scoped lang="scss">

.memory {
  padding-bottom: 1rem;

  .memory-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    width: 100%;
    justify-items: center;
    column-gap: 10px;
    row-gap: 10px;
  }

  .memory-card {
    width: 100%;
    height: 180px;
    font-size: 3rem;
    position: relative;
    transition: opacity 0.7s;
  }

  .back,
  .front {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    backface-visibility: hidden;
    transform: translateZ(0);
    transition: transform 0.8s;
    transform-style: preserve-3d;
    border-radius: 5px;
    background: white;
  }

  .memory-card img {
    max-width: 90%;
  }

  .memory-card .front {
    background-image: url("@/assets/memory/memory_cover.png");
    background-size: 85%;
    background-position: center;
    background-repeat: no-repeat;
  }

  .memory-card .back {
    transform: rotateY(-180deg);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .memory-card.matchingPair .front,
  .memory-card.flipped .front {
    transform: rotateY(180deg);
  }

  .memory-card.matchingPair .back,
  .memory-card.flipped .back {
    transform: rotateY(0deg);
  }

  .memory-card.matchingPair {
    opacity: 0.3;
  }
}
</style>
